"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Card, CardBody } from "@/components/ui/Card";
import type { RecentDonor } from "@/lib/api-types";
import { cn, formatCurrency } from "@/lib/utils";

const ROTATE_MS = 4000;
const MAX_ROTATING = 5;
const MAX_STATIC = 3;

function formatRecentDonorDate(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(date);
}

function DonorRow({
  donor,
  compact,
}: {
  donor: RecentDonor;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex w-full items-center justify-between gap-3",
        compact ? "py-1.5" : "py-1"
      )}
    >
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{donor.name}</p>
        <p className="text-xs text-muted">
          {formatRecentDonorDate(donor.createdAt)}
        </p>
      </div>
      <p className="shrink-0 font-bold tabular-nums text-primary">
        {formatCurrency(donor.amount)}
      </p>
    </div>
  );
}

type RecentDonorsTeaserProps = {
  donors: RecentDonor[];
  /** When the recent-donors API failed (distinct from an empty successful list). */
  unavailable?: boolean;
};

export function RecentDonorsTeaser({
  donors,
  unavailable = false,
}: RecentDonorsTeaserProps) {
  const reduced = useReducedMotion();
  const rotating = donors.slice(0, MAX_ROTATING);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [rotating.length]);

  useEffect(() => {
    if (reduced || paused || rotating.length <= 1) return;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % rotating.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [reduced, paused, rotating.length]);

  if (unavailable) {
    return (
      <section className="py-10">
        <div className="mx-auto max-w-lg px-4">
          <h3 className="mb-4 text-center text-xl font-bold text-foreground">
            Recent Donors
          </h3>
          <p className="text-center text-sm text-muted" role="status">
            Recent donors unavailable.
          </p>
        </div>
      </section>
    );
  }

  if (rotating.length === 0) return null;

  const active = rotating[Math.min(index, rotating.length - 1)];
  const staticDonors = rotating.slice(0, MAX_STATIC);

  return (
    <section className="py-10">
      <div className="mx-auto max-w-lg px-4">
        <h3 className="mb-4 text-center text-xl font-bold text-foreground">
          Recent Donors
        </h3>
        <Card>
          <CardBody
            className="px-4 py-3"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocusCapture={() => setPaused(true)}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setPaused(false);
              }
            }}
          >
            {reduced ? (
              <div className="divide-y divide-border" aria-live="polite">
                {staticDonors.map((donor) => (
                  <DonorRow
                    key={`${donor.name}-${donor.amount}-${donor.createdAt}`}
                    donor={donor}
                    compact
                  />
                ))}
              </div>
            ) : (
              <div className="flex min-h-[72px] flex-col justify-center">
                <div className="relative min-h-[52px]" aria-live="polite">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={`${active.name}-${active.amount}-${active.createdAt}-${index}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <DonorRow donor={active} />
                    </motion.div>
                  </AnimatePresence>
                </div>
                {rotating.length > 1 ? (
                  <div
                    className="mt-2 flex items-center justify-center gap-1.5"
                    role="tablist"
                    aria-label="Recent donors"
                  >
                    {rotating.map((donor, i) => (
                      <button
                        key={`${donor.name}-${donor.amount}-${i}`}
                        type="button"
                        role="tab"
                        aria-selected={i === index}
                        aria-label={`Show donor ${i + 1} of ${rotating.length}`}
                        className={cn(
                          "h-1.5 rounded-full transition-all",
                          i === index
                            ? "w-4 bg-primary"
                            : "w-1.5 bg-border hover:bg-muted"
                        )}
                        onClick={() => setIndex(i)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </section>
  );
}
